import { Text, TextField } from "components/commons";
import { dateFormat, formatAmount, timeFormat } from "utils";
import styles from "./shift-details-stock-inventory.module.scss";
import { prettifyDryStock, prettifyEmployeePosition } from "utils/pretty.utils";
import Big from "big.js";
import { EmployeePosition } from "enums";
import { filter } from "lodash";

export const computeExpectedInventory = (beginning, delivery, sales, returnInv) => {
  return Number(Big(beginning).add(delivery).minus(sales).add(returnInv));
};

export const dryStockSalesMapper = ({ val, isLubricant }) => {
  const {
    name,
    variant,
    originalPricePerItem,
    totalDiscount,
    // premiumDiscount,
    quantity,
    originalTotalAmount,
    // netTotalAmount,
    category,
    // grossTotalAmount,
    finalAmount,
    vatExemptAmount,
    sku,
  } = val;
  // const discount = grossTotalAmount - netTotalAmount;
  // const finalDiscount = discount <= 0 ? premiumDiscount?.discountPremium || 0 : -discount;

  // const finalAmount = originalTotalAmount - totalDiscount;

  const fields = {
    itemCode: (
      <>
        <Text className={styles.content}>{sku ?? ""}</Text>
      </>
    ),
    product: (
      <>
        <Text className={styles.content}>{name ?? ""}</Text>
      </>
    ),
    variant: (
      <>
        <Text className={styles.content}>{variant ?? ""}</Text>
      </>
    ),
    unitPrice: (
      <>
        <Text className={styles.content}>{formatAmount(originalPricePerItem ?? 0)}</Text>
      </>
    ),
    quantitySold: (
      <>
        <Text className={styles.content}>{quantity ?? 0}</Text>
      </>
    ),
    amount: (
      <>
        <Text className={styles.content}>{formatAmount(originalTotalAmount ?? 0)}</Text>
      </>
    ),
    discounts: (
      <>
        <Text className={styles.content}>
          {totalDiscount < 0 ? "-" : ""}
          {formatAmount(Math.abs(totalDiscount ?? 0))}
        </Text>
      </>
    ),
    finalAmount: (
      <>
        <Text className={styles.content}>{formatAmount(finalAmount ?? 0)}</Text>
      </>
    ),
    vatExempt: (() => {
      const vatVal = vatExemptAmount ?? 0;
      return (
        <>
          <Text className={vatVal < 0 ? styles.negative : styles.content}>
            {vatVal < 0 ? `-${formatAmount(-vatVal)}` : formatAmount(vatVal)}
          </Text>
        </>
      );
    })(),
  };

  return isLubricant
    ? {
        productCategory: (
          <>
            <Text className={styles.content}>{category ?? ""}</Text>
          </>
        ),
        ...fields,
      }
    : fields;
};

export const dryStockDeliveriesMapper = ({ val }) => {
  const { deliveryId, deliveredAt, poReferenceNumber, plateNumber, witnesses } = val;
  let witness = [];
  if (witnesses && witnesses.length > 0) {
    const seen = new Set();
    const uniqueWitnesses = witnesses.filter((e) => {
      const key = `${e.name}|${e.position}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    filter(uniqueWitnesses, (e) => {
      const cashier = e.position === prettifyEmployeePosition(EmployeePosition.CASHIER);
      if (cashier) {
        witness.push(e.name);
      }
      return cashier;
    });
    filter(uniqueWitnesses, (e) => {
      const crew = e.position !== prettifyEmployeePosition(EmployeePosition.CASHIER);
      if (crew) {
        witness.push(e.name);
      }
      return crew;
    });
  }

  return {
    deliveryId: (
      <>
        <Text className={styles.content}>{deliveryId ?? "0"}</Text>
      </>
    ),
    dateAndTime: (
      <>
        <Text className={styles.content}>{`${dateFormat(deliveredAt)} ${timeFormat(
          deliveredAt,
        )}`}</Text>
      </>
    ),
    poReferenceNumber: (
      <>
        <Text className={styles.content}>{poReferenceNumber ?? ""}</Text>
      </>
    ),
    plateNumber: (
      <>
        <Text className={styles.content}>{plateNumber ?? ""}</Text>
      </>
    ),
    witnesses: (
      <>
        {witness.map((item, i) => (
          <Text className={styles.content}>{item ?? ""}</Text>
        ))}
      </>
    ),
  };
};

export const dryStockDeliveryProductsMapper = ({ val }) => {
  const {
    deliveryId,
    product,
    variant,
    poReferenceNumber,
    quantityDelivered,
    quantityAfterReceiving,
    quantityBeforeReceiving,
    sku,
  } = val;

  const quantityL = Number(Big(quantityAfterReceiving).minus(quantityBeforeReceiving));
  const variance = Number(Big(quantityL).minus(quantityDelivered));
  const quantity = Number(Big(quantityBeforeReceiving).add(quantityDelivered));
  let variancePercentage = 0;
  if (quantity > 0) {
    variancePercentage = Number(Big(variance).div(quantity).mul(100));
  }

  if (variancePercentage > 0) {
    variancePercentage = `(+${variancePercentage.toFixed(2)}%)`;
  } else {
    variancePercentage = `(${variancePercentage.toFixed(2)}%)`;
  }

  return {
    deliveryId: (
      <>
        <Text className={styles.content}>{deliveryId ?? "0"}</Text>
      </>
    ),
    poReferenceNumber: (
      <>
        <Text className={styles.content}>{poReferenceNumber ?? ""}</Text>
      </>
    ),
    itemCode: (
      <>
        <Text className={styles.content}>{sku ?? ""}</Text>
      </>
    ),
    product: (
      <>
        <Text className={styles.content}>{product ?? "-"}</Text>
      </>
    ),
    variant: (
      <>
        <Text className={styles.content}>{variant ?? "-"}</Text>
      </>
    ),
    beforeReceiving: (
      <>
        <Text className={styles.content}>{quantityBeforeReceiving ?? ""}</Text>
      </>
    ),
    quantityDelivered: (
      <>
        <Text className={styles.content}>{quantityDelivered ?? ""}</Text>
      </>
    ),
    afterReceiving: (
      <>
        <Text className={styles.content}>{quantityAfterReceiving ?? ""}</Text>
      </>
    ),
    variance: (
      <>
        <Text
          className={
            variance > 0 ? styles.positive : variance < 0 ? styles.negative : styles.content
          }
        >
          {variance > 0
            ? `+${variance} ${variancePercentage}`
            : variance < 0
              ? `${variance} ${variancePercentage}`
              : variance}
        </Text>
      </>
    ),
  };
};

export const dryStockMovementMapper = ({ val, isEdit, inventory, setInventory }) => {
  const {
    beginningInventory,
    endingInventory,
    salesInventory,
    returnInventory,
    shiftInventoryId,
    product,
    variant,
    type,
    totalQuantityDelivered,
    sku,
  } = val;

  const initItem = {
    shiftInventoryId: shiftInventoryId ?? 0,
    beginningInventory: beginningInventory ?? "",
    endingInventory: endingInventory ?? "",
  };

  const item = inventory.find((item) => item.shiftInventoryId === shiftInventoryId) ?? initItem;

  const computeVariance = (beginning, expectedEnding) => {
    return Number(
      Big(!isNaN(beginning) ? beginning : 0).minus(!isNaN(expectedEnding) ? expectedEnding : 0),
    );
  };

  const currentBeginningInventory = isEdit ? item.beginningInventory : beginningInventory;
  const isBeginningInventoryBlank =
    currentBeginningInventory == null || currentBeginningInventory === "";

  const expectedEndingInventory = computeExpectedInventory(
    currentBeginningInventory || 0,
    totalQuantityDelivered,
    salesInventory,
    returnInventory,
  );

  const variance = computeVariance(
    // isEdit ? (item.endingInventory === "" ? 0 : item.endingInventory) : endingInventory,
    item.endingInventory || 0,
    expectedEndingInventory,
  );

  const varianceColor = isNaN(variance)
    ? ""
    : variance === 0
      ? "black"
      : variance > 0
        ? "#459d4b"
        : "#f45047";

  const replaceInventoryItem = (id, newItem) => {
    if (inventory) {
      setInventory((prevInventory) =>
        prevInventory.map((item) => (item.shiftInventoryId === id ? newItem : item)),
      );
    } else {
      setInventory([...newItem]);
    }
  };

  const isNumericString = (val) => {
    return typeof val === "string" && /^-?\d+(\.\d+)?$/.test(val);
  };

  const trimLeadingZeros = (val) => {
    if (typeof val !== "string") return val;

    // allow typing "-"
    if (val === "-" || val === "") return val;

    return val.replace(/^(-?)0+(?!$)/, "$1");
  };

  return {
    itemCode: (
      <>
        <Text className={styles.content}>{sku ?? "-"}</Text>
      </>
    ),
    product: (
      <>
        <Text className={styles.content}>{product ?? "-"}</Text>
      </>
    ),
    variant: (
      <>
        <Text className={styles.content}>{variant ?? "-"}</Text>
      </>
    ),
    type: (
      <>
        <Text className={styles.content}>{prettifyDryStock(type) ?? "-"}</Text>
      </>
    ),
    actualBeginningInventory: (
      <>
        {isEdit ? (
          <TextField
            value={item.beginningInventory ?? ""}
            onChange={(_, { value }) => {
              if (isNumericString(value) || value === "") {
                let newItem = item;
                newItem.beginningInventory = value === "" ? "" : trimLeadingZeros(value);
                replaceInventoryItem(item.id, newItem);
              }
            }}
            onBlur={(_, { value }) => {
              let newItem = item;
              newItem.beginningInventory =
                value === "" ? beginningInventory : trimLeadingZeros(value);
              replaceInventoryItem(item.id, newItem);
            }}
          />
        ) : (
          <Text className={styles.content}>{item.beginningInventory}</Text>
        )}
      </>
    ),
    delivery: (
      <>
        <Text className={styles.content}>{totalQuantityDelivered}</Text>
      </>
    ),
    sales: (
      <>
        <Text className={styles.content}>{salesInventory}</Text>
      </>
    ),
    returns: (
      <>
        <Text className={styles.content}>{returnInventory}</Text>
      </>
    ),
    expectedEndingInventory: (
      <>
        <Text className={styles.content}>
          {isBeginningInventoryBlank ? "" : expectedEndingInventory}
        </Text>
      </>
    ),
    actualEndingInventory: (
      <>
        {isEdit ? (
          <TextField
            value={item.endingInventory ?? ""}
            onChange={(_, { value }) => {
              if (isNumericString(value) || value === "" || value === "-") {
                let newItem = item;
                newItem.endingInventory = value === "" ? "" : trimLeadingZeros(value);
                replaceInventoryItem(item.id, newItem);
              }
            }}
            onBlur={(_, { value }) => {
              let newItem = item;
              newItem.endingInventory = value === "" ? endingInventory : trimLeadingZeros(value);
              replaceInventoryItem(item.id, newItem);
            }}
          />
        ) : (
          // <Text className={styles.content}>{endingInventory}</Text>
          <Text className={styles.content}>{item.endingInventory}</Text>
        )}
      </>
    ),
    variance: (
      <>
        <Text color={varianceColor}>{variance}</Text>
      </>
    ),
  };
};
